import { CreateLegalEntityRequest, LegalEntityDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CustomTextFieldInput } from '../../../shared/CustomTextFieldInput/CustomTextFieldInput';
import { useSaveLegalEntity } from '../../../apis/shops-api';

type Props = {
  isOpen: boolean;
  item: LegalEntityDto | null;
  onClose: () => void;
};

type LegalEntityFormData = CreateLegalEntityRequest;

const LegalEntityModal: React.FC<Props> = ({ item, isOpen, onClose }) => {
  const { t } = useTranslation();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<LegalEntityFormData>({
    defaultValues: item ? item : {},
  });

  useEffect(() => {
    if (item) {
      reset(item);
    } else {
      reset({
        legalEntityName: '',
        businessId: '',
        vatId: '',
      });
    }
  }, [item, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const saveEntity = useSaveLegalEntity({ onSettled: handleClose });

  const handleForm: SubmitHandler<LegalEntityFormData> = (formData) => {
    saveEntity.mutate({ legalEntityId: item?.legalEntityId, request: formData });
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle>{item ? t('global.edit') : t('global.add')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(handleForm)}>
          <Box sx={{ my: 2 }}>
            <CustomTextFieldInput
              control={control}
              rules={{ required: t('global.fieldIsRequired'), minLength: 1, maxLength: 250 }}
              error={errors.legalEntityName}
              label={t('Name')}
              name="legalEntityName"
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <CustomTextFieldInput
              control={control}
              rules={{ required: t('global.fieldIsRequired') }}
              error={errors.businessId}
              label={t('global.businessId')}
              name="businessId"
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <CustomTextFieldInput
              control={control}
              rules={{ required: t('global.fieldIsRequired') }}
              error={errors.vatId}
              label={t('global.vatId')}
              name="vatId"
            />
          </Box>
          <DialogActions>
            <Button variant="outlined" color="error" onClick={handleClose} disabled={saveEntity.isLoading}>
              {t('global.cancel')}
            </Button>
            <Button variant="contained" color="success" type="submit" disabled={saveEntity.isLoading}>
              {t('global.save')}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LegalEntityModal;
